import axios from 'axios';
import { get } from 'lodash';

const client = axios.create();

client.interceptors.request.use(async (config) => {
    const token = await window.shopify.idToken();
    config.headers.Authorization = `Bearer ${token}`;

    return config;
})

client.interceptors.response.use(
    (response) => response,
    (error) => {
        if (get(error, 'response.status') === 403) {
            const message = get(error, 'response.data.message') || "You don't have permission to do that";

            window.shopify.toast.show(message, { duration: 5000, isError: true });
        }

        return Promise.reject(error);
    },
)

const Api = {
    upsertCustomer: async (params) => {
        if ( params.id ) {
            return client.put('/ajax/customers/' + params.id, params);
        }

        return client.post('/ajax/customers', params);
    },

    listUnapprovedCustomers: async (page = 1) => {
        return client.get('/ajax/customers/unapproved', {
            params: { page },
        });
    },

    findCustomer: async (query, page) => {
        return client.get('/ajax/customers/find', {
            params: {q: query, page},
        });
    },

    getCustomer: async (id) => {
        return client.get(`/ajax/customers/${id}`);
    },

    addToRegistry: async (type, id) => {
        return client.post(`/ajax/registry/add`,{
            type,
            id
        });
    },

    getLocations: async (customerId, page = 1, search = '') => {
        return client.get(`/ajax/customers/${customerId}/locations?page=${page}&q=${search}`);
    },

    getLocation: async (locationId) => {
        return client.get(`/ajax/locations/${locationId}`);
    },

    updateLocation: async(locationId, payload) => {
        return client.put(`/ajax/locations/${locationId}`, payload);
    },

    createLocation: async(customerId, payload) => {
        return client.post(`/ajax/customers/${customerId}/locations`, payload);
    },

    getShippingMethods: async () => {
        return client.get(`/ajax/customers/shipping_methods`);
    },

    getCollectionsMethod: async () => {
        return client.get(`/ajax/collections`);
    },

    getPaymentTerms: async () => {
        return client.get(`/ajax/customers/payment_terms`);
    },

    getProducts: async (query, locationId, includeDrafts, includeArchived, filterCollections) => {
        return client.get('/ajax/products', {
            params: {
                q: query,
                per_page: 100,
                location_id: locationId,
                include_drafts: includeDrafts,
                include_archived: includeArchived,
                filter_collections: filterCollections
            },
        });
    },

    getDraftOrder: async (id) => {
        return client.get(`/ajax/orders/draft/${id}`);
    },

    placeDraftOrder: async (params) => {
        return client.post('/ajax/orders/draft', params);
    },

    updateLocationAfterOrder: async (locationId, data) => {
        return client.put(`/ajax/locations/${locationId}/after-order`, data);
    },

    toggleCustomerOnHold: async (customerId) => {
        return client.put('/ajax/customers/' + customerId + '/toggle_on_hold', {});
    },

    createLocationCustomer: async (locationId, email) => {
        return client.post(`/ajax/locations/${locationId}/customer`, { email });
    },

    getInvoices: (customerId, page = 1) => {
        return client.get(`/ajax/customers/${customerId}/invoices?page=${page}`);
    },

    downloadInvoice: (customerId, invoiceId) => {
        return client.post('/ajax/customers/' + customerId + '/download_invoice/' + invoiceId, {}, {
            responseType: 'blob'
        });
    },

    createDiscountCode: (data) => client.post('/ajax/discount-codes', data),

    getDiscountCode: (id) => client.get(`/ajax/discount-codes/${id}`),

    updateDiscountCode: (id, data) => client.put(`/ajax/discount-codes/${id}`, data),

    listDiscountCodes: (page = 1) => client.get('/ajax/discount-codes', {
        params: { page }
    }),

    applyDiscountCode: (customerId, code, products) => client.post('/ajax/discount-codes/apply', {
        code,
        products,
        customer_id: customerId,
    }),

    hasCustomerOrders: (customerId) => client.get('/ajax/customers/' + customerId + '/has_orders'),

    deleteCustomer: async (customerId) => {
        return client.delete(`/ajax/customers/${customerId}`);
    },

    sendQuote: (data) => client.post('/ajax/orders/send-quote', data),

    deleteDiscountCode: (id) => client.delete(`/ajax/discount-codes/${id}`),

    getDiscountedLineItems: (id, page) => client.get(`/ajax/discount-codes/${id}/line-items?page=${page}`),

    findUsers: (params) => client.get('/ajax/users', {
        params: params,
    }),

    getUser: (id) => client.get(`/ajax/users/${id}`),

    getMe: () => client.get(`/ajax/users/me`),

    toggleAdminPrivileges: (userId) => client.put(`/ajax/users/${userId}/toggle-admin`),

    toggleOnHoldPrivileges: (userId) => client.put(`/ajax/users/${userId}/toggle-on-hold`),

    updateCustomerEmails: async (customerId, data) => {
        return client.put(`/ajax/customers/${customerId}/emails`, data);
    },

    mergeAccount: async (customerId, withAccount) => {
        return client.post(`/ajax/customers/${customerId}/merge`, {
            with_account: withAccount,
        });
    },

    getUsersForRegistry: () => client.get('/ajax/registry/users'),

    getUserActivities: (userId, page) => client.get(`/ajax/registry/activities/${userId}?page=${page}`),

    getBillingAddresses: async (search, page) => {
        return client.get('/ajax/customers/billing-addresses', {
            params: { q: search, page: page },
        });
    },

    getBillingAddress: async (id) => {
        return client.get(`/ajax/customers/billing-addresses/${id}`);
    },

    createBillingAddress: async (data) => {
        return client.post('/ajax/customers/billing-addresses', data);
    },

    updateBillingAddress: async (id, data) => {
        return client.put(`/ajax/customers/billing-addresses/${id}`, data);
    },

    deleteBillingAddress: async (id) => {
        return client.delete(`/ajax/customers/billing-addresses/${id}`);
    },
}

export default Api;
